// The chat surface shared by Investigations (InvestigationChat) and Policies
// (PolicyChat): a thread rail, streamed SSE answers, server-numbered [n]
// citations with hover cards and a Sources row, and a composer. Extracted
// from InvestigationChat; everything that was specific to an investigation
// (its API, where a citation opens, the mini player, the copy) comes in as
// props so the two chats look and behave the same.
//
// Citations [n] are numbered globally per thread and resolved against every
// message's citation list, so a click on [3] in turn four opens the same
// passage it meant in turn one.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { relativeTime } from "../relativeTime";
import {
  ArrowUpIcon,
  PlusIcon,
  StopIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  Bars3BottomLeftIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  CheckIcon,
  XMarkIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import AiDisclaimer, { AiDisclaimerKind } from "../../Investigations/AiDisclaimer";
import { ChatCitation } from "../../../db/schema";
import { StreamedMarkdown } from "../../Investigations/chatMarkdown";
import CitationPopover, { CitationHover } from "../../Investigations/CitationPopover";
import Button from "../Button";
import ResizeHandle from "../ResizeHandle";
import { useResizablePanel } from "../../../hooks/useResizablePanel";

export type IconComponent = React.FC<{ className?: string; title?: string }>;

export type ChatThreadLike = {
  id: string;
  title: string | null;
  last_message_at: Date | string | null;
  created_at: Date | string | null;
  // Set when the thread is bound to one item (an investigation call).
  focus_call_id?: string | null;
};

export type ChatMessageLike = {
  id: string;
  role: string;
  content: string;
  citations: ChatCitation[] | null;
  created_at: Date | string | null;
};

type ToolTrace = { name: string; summary: string; done: boolean };
type LiveMessage = ChatMessageLike & { streaming?: boolean; tools?: ToolTrace[] };

export interface ChatClient<T extends ChatThreadLike = ChatThreadLike> {
  listThreads: () => Promise<T[]>;
  createThread: (opts: { focusId?: string }) => Promise<T>;
  messages: (threadId: string) => Promise<{ thread: T; messages: ChatMessageLike[] }>;
  archiveThread: (threadId: string) => Promise<unknown>;
  // POST { message } here; the response is the SSE stream.
  streamUrl: (threadId: string) => string;
}

export type ChatSuggestion = { icon: IconComponent; text: string };

export interface ChatCopy {
  // "Cited answers over every call, document, and spreadsheet in this investigation"
  tagline: string;
  placeholder: string;
  compactPlaceholder: string;
  emptyTitle: string;
  compactEmptyTitle: string;
  emptyBlurb: string;
  suggestions: ChatSuggestion[];
  // Focused threads (optional).
  focusPlaceholder?: string;
  focusEmptyTitle?: string;
  focusSuggestions?: ChatSuggestion[];
  focusNote?: string;
}

export interface ChatPanelProps {
  client: ChatClient<any>;
  onOpenCitation: (c: ChatCitation) => void;
  copy: ChatCopy;
  disclaimerKind: AiDisclaimerKind;
  railStorageKey: string;
  // Icons for the tool trace lines, keyed by tool name.
  toolIcons?: Record<string, IconComponent>;
  // The assistant avatar tile.
  avatarIcon?: IconComponent;
  // Compact = side-rail mode. Full = the main tab.
  compact?: boolean;
  onClose?: () => void;
  // Start a fresh thread bound to this item.
  focus?: { id: string; label: string; icon?: IconComponent };
  // Open this thread first (deep link).
  initialThreadId?: string | null;
  onThreadChange?: (threadId: string | null) => void;
  // Space to leave at the bottom (a docked mini player).
  bottomInset?: number;
  // Replaces the composer with a notice (read-only views).
  readOnlyNotice?: string | null;
  // Source chip label and icon per citation.
  labelFor?: (c: ChatCitation) => string;
  sourceIcon?: (c: ChatCitation) => IconComponent;
}

const defaultLabel = (c: ChatCitation) => c.label.replace(/ @ .*$/, "").slice(0, 28);

const ChatPanel: React.FC<ChatPanelProps> = ({
  client,
  onOpenCitation,
  copy,
  disclaimerKind,
  railStorageKey,
  toolIcons = {},
  avatarIcon: AvatarIcon = MagnifyingGlassIcon,
  compact,
  onClose,
  focus,
  initialThreadId,
  onThreadChange,
  bottomInset = 0,
  readOnlyNotice,
  labelFor = defaultLabel,
  sourceIcon,
}) => {
  const [threads, setThreads] = useState<ChatThreadLike[]>([]);
  const [threadId, setThreadIdState] = useState<string | null>(null);
  const setThreadId = (next: string | null | ((cur: string | null) => string | null)) =>
    setThreadIdState((cur) => {
      const v = typeof next === "function" ? next(cur) : next;
      if (v !== cur) onThreadChange?.(v);
      return v;
    });
  const [messages, setMessages] = useState<LiveMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [railOpen, setRailOpen] = useState(!compact);
  const railPanel = useResizablePanel({
    storageKey: railStorageKey,
    defaultWidth: 256,
    minWidth: 200,
    maxWidth: 480,
    side: "right",
    maxViewportFraction: 0.35,
  });
  const [loadingThread, setLoadingThread] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const stickToBottom = useRef(true);
  const [followingStream, setFollowingStream] = useState(true);
  const lastScrollTop = useRef(0);
  const skipNextLoad = useRef(false);
  const FocusIcon = focus?.icon;

  // The URL-driven thread id is read through a ref so that the list load
  // does not re-run (and re-pick the latest thread) every time the host
  // rewrites the URL, which is exactly what happens after "New chat".
  const initialThreadIdRef = useRef(initialThreadId);
  initialThreadIdRef.current = initialThreadId;
  const pickedInitial = useRef(false);

  const loadThreads = useCallback(async () => {
    const t = await client.listThreads();
    setThreads(t);
    // Only the first load picks a thread: focused mode begins with a fresh
    // one; otherwise the deep linked thread, else the latest. Later reloads
    // (after a send, an archive, a new chat) keep whatever is selected, so a
    // deliberately empty selection stays empty.
    if (pickedInitial.current) return;
    pickedInitial.current = true;
    setThreadIdState((cur) => {
      if (cur) return cur;
      const wanted = initialThreadIdRef.current;
      const next = focus ? null : (wanted && t.some((x) => x.id === wanted) ? wanted : t[0]?.id || null);
      if (next !== cur) onThreadChange?.(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, focus?.id]);

  useEffect(() => {
    pickedInitial.current = false;
    loadThreads().catch(() => {});
  }, [loadThreads]);

  // A later change of the URL to a specific thread (back/forward, a link)
  // selects it; a change to no thread is left alone.
  useEffect(() => {
    if (!initialThreadId) return;
    setThreadIdState((cur) => (cur === initialThreadId ? cur : initialThreadId));
  }, [initialThreadId]);

  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (skipNextLoad.current) {
      skipNextLoad.current = false;
      return;
    }
    if (!threadId) return setMessages([]);
    setMessages([]);
    setFollowing(true);
    lastScrollTop.current = 0;
    setLoadingThread(true);
    client
      .messages(threadId)
      .then((d) => {
        loadedFor.current = threadId;
        setMessages(d.messages);
      })
      .catch(() => setMessages([]))
      .finally(() => setLoadingThread(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, threadId]);

  const scrolledThread = useRef<string | null | undefined>(undefined);
  useLayoutEffect(() => {
    if (!stickToBottom.current) return;
    const threadChanged = scrolledThread.current !== threadId;
    if (messages.length > 0 && loadedFor.current === threadId) scrolledThread.current = threadId;
    const el = scrollRef.current;
    if (threadChanged || streaming) {
      if (el) el.scrollTop = el.scrollHeight;
    } else {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, streaming, threadId]);
  const setFollowing = (v: boolean) => {
    if (stickToBottom.current === v) return;
    stickToBottom.current = v;
    setFollowingStream(v);
  };
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const movedUp = el.scrollTop < lastScrollTop.current - 1;
    lastScrollTop.current = el.scrollTop;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    if (movedUp) setFollowing(false);
    else if (atBottom) setFollowing(true);
  };
  const onWheel = (e: React.WheelEvent) => {
    if (e.deltaY < 0) setFollowing(false);
  };
  const touchStartY = useRef(0);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0]?.clientY ?? 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const y = e.touches[0]?.clientY ?? 0;
    if (y > touchStartY.current + 4) setFollowing(false);
  };
  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    setFollowing(true);
    el.scrollTop = el.scrollHeight;
  };
  const followStream = useCallback(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  const citationIndex = useMemo(() => {
    const map = new Map<number, ChatCitation>();
    for (const m of messages) for (const c of m.citations || []) map.set(c.n, c);
    return map;
  }, [messages]);

  const [hoverCite, setHoverCite] = useState<CitationHover | null>(null);
  const citeCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleCiteClose = () => {
    if (citeCloseTimer.current) clearTimeout(citeCloseTimer.current);
    citeCloseTimer.current = setTimeout(() => setHoverCite(null), 180);
  };
  const cancelCiteClose = () => {
    if (citeCloseTimer.current) clearTimeout(citeCloseTimer.current);
    citeCloseTimer.current = null;
  };
  const cite = (n: number) => {
    const c = citationIndex.get(n);
    return (
      <button
        data-tour="chat-citation"
        onClick={() => c && onOpenCitation(c)}
        disabled={!c}
        title={c ? undefined : `Citation ${n}`}
        onMouseEnter={(e) => {
          if (!c) return;
          cancelCiteClose();
          const r = e.currentTarget.getBoundingClientRect();
          const below = r.bottom + 200 < window.innerHeight;
          setHoverCite({ c, x: Math.min(r.left, window.innerWidth - 400), y: below ? r.bottom + 6 : r.top - 6, below });
        }}
        onMouseLeave={scheduleCiteClose}
        className={cn(
          "inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-md text-[10px] font-semibold tabular-nums align-baseline transition-colors",
          c ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-500 hover:text-white" : "bg-base-200 text-base-content/40"
        )}
      >
        {n}
      </button>
    );
  };

  const newThread = () => {
    discardIfEmpty(threadId);
    setThreadId(null);
    setMessages([]);
    textareaRef.current?.focus();
  };

  const discardIfEmpty = (id: string | null) => {
    if (!id || streaming || loadingThread || messages.length > 0) return;
    setThreads((prev) => prev.filter((t) => t.id !== id));
    client.archiveThread(id).catch(() => {});
  };

  const switchThread = (id: string) => {
    if (id === threadId) return;
    discardIfEmpty(threadId);
    setThreadId(id);
  };

  const archive = async (t: ChatThreadLike) => {
    await client.archiveThread(t.id);
    setThreads((prev) => prev.filter((x) => x.id !== t.id));
    if (t.id === threadId) {
      setThreadId(null);
      setMessages([]);
    }
  };

  const send = async (text?: string) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    // A chat isn't created on the server until the first message is sent,
    // so "New chat" never leaves empty threads behind.
    const tid: string =
      threadId ??
      (await (async () => {
        const t = await client.createThread(focus ? { focusId: focus.id } : {});
        setThreads((prev) => [t, ...prev]);
        skipNextLoad.current = true;
        loadedFor.current = t.id;
        setThreadId(t.id);
        return t.id as string;
      })());
    setInput("");
    setStreaming(true);
    setFollowing(true);
    const userMsg: LiveMessage = { id: `u-${Date.now()}`, role: "user", content: question, citations: null, created_at: new Date() };
    const assistant: LiveMessage = { id: `a-${Date.now()}`, role: "assistant", content: "", citations: [], created_at: new Date(), streaming: true, tools: [] };
    setMessages((prev) => [...prev, userMsg, assistant]);
    const patch = (fn: (m: LiveMessage) => LiveMessage) =>
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = fn(next[next.length - 1]);
        return next;
      });

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const response = await fetch(client.streamUrl(tid), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: controller.signal,
        body: JSON.stringify({ message: question }),
      });
      if (!response.ok || !response.body) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.error || "Chat request failed");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          let event = "message";
          let data = "";
          for (const line of frame.split("\n")) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) data += line.slice(6);
          }
          if (!data) continue;
          const parsed = JSON.parse(data);
          if (event === "text") patch((m) => ({ ...m, content: m.content + parsed }));
          else if (event === "citations") patch((m) => ({ ...m, citations: parsed }));
          else if (event === "tool_start") patch((m) => ({ ...m, tools: [...(m.tools || []), { name: parsed.name, summary: parsed.summary, done: false }] }));
          else if (event === "tool_end")
            patch((m) => {
              const tools = [...(m.tools || [])];
              const idx = tools.map((t) => t.done).lastIndexOf(false);
              if (idx >= 0) tools[idx] = { ...tools[idx], done: true };
              return { ...m, tools };
            });
          else if (event === "error") patch((m) => ({ ...m, content: m.content || parsed.error }));
        }
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") patch((m) => ({ ...m, content: m.content || error?.message || "Something went wrong." }));
    } finally {
      patch((m) => ({ ...m, streaming: false, tools: (m.tools || []).map((t) => ({ ...t, done: true })) }));
      setStreaming(false);
      abortRef.current = null;
      loadThreads().catch(() => {});
    }
  };

  const activeThread = threads.find((t) => t.id === threadId);
  const empty = messages.length === 0 && !loadingThread;
  const suggestions = (focus && copy.focusSuggestions) || copy.suggestions;

  // ---- Rendering ---------------------------------------------------------

  const threadBadge = (t: ChatThreadLike) =>
    t.focus_call_id && FocusIcon ? <FocusIcon className="size-3 shrink-0 text-blue-500" title="Focused on one item" /> : null;

  const rail = !compact && (
    <aside
      style={{ width: railOpen ? railPanel.width : 0 }}
      className={cn("relative shrink-0 border-r border-base-300 bg-base-200/50 flex flex-col overflow-hidden", !railPanel.isResizing && "transition-[width] duration-200")}
    >
      {railOpen && <ResizeHandle edge="right" className="!right-0" active={railPanel.isResizing} {...railPanel.handleProps} />}
      <div className="p-3">
        <Button variant="outline" size="sm" onClick={newThread} className="w-full justify-start rounded-xl bg-base-100 font-medium">
          <PlusIcon className="size-4" /> New chat
        </Button>
      </div>
      <div className="px-3 pb-1 text-[11px] uppercase tracking-wider text-base-content/40 font-semibold">Chats</div>
      <ul className="flex-1 overflow-auto px-2 pb-3 space-y-0.5">
        {threads.length === 0 && <li className="px-2 py-2 text-xs text-base-content/50">Nothing yet. Ask your first question.</li>}
        {threads.map((t) => (
          <li key={t.id} className="group relative">
            <button
              onClick={() => switchThread(t.id)}
              className={cn(
                "w-full text-left px-3 py-2 rounded-lg text-sm truncate pr-8 transition-colors border",
                t.id === threadId
                  ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border-transparent"
                  : "border-transparent text-base-content/70 hover:bg-base-100/70 hover:text-base-content"
              )}
            >
              <span className="flex items-center gap-1.5 truncate">
                {threadBadge(t)}
                <span className="truncate">{t.title || "Untitled"}</span>
              </span>
              <span className="block text-[11px] text-base-content/40">{relativeTime(t.last_message_at || t.created_at)}</span>
            </button>
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 text-base-content/40 hover:text-red-600 dark:hover:text-red-400 transition-opacity"
              title="Archive"
              onClick={async (e) => {
                e.stopPropagation();
                await archive(t);
              }}
            >
              <TrashIcon className="size-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );

  const composer = (
    <div className={cn("shrink-0", compact ? "px-2 pb-2" : "px-4 pb-4")}>
      <form
        data-tour="chat-composer"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className={cn(
          "mx-auto w-full rounded-2xl border border-base-300 bg-base-100 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.7)] focus-within:border-blue-500 transition-colors",
          compact ? "max-w-none" : "max-w-3xl"
        )}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={focus ? copy.focusPlaceholder || copy.placeholder : compact ? copy.compactPlaceholder : copy.placeholder}
          className="block w-full resize-none bg-transparent outline-none px-4 pt-3.5 pb-2 text-[15px] leading-6 text-base-content placeholder:text-base-content/40"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <div className="flex items-center justify-between px-2.5 pb-2.5">
          <span className="text-[11px] text-base-content/40 pl-1.5 hidden sm:inline">Enter to send · Shift+Enter for a new line</span>
          {streaming ? (
            <Button type="button" variant="primary" onClick={() => abortRef.current?.abort()} className="!min-h-0 !h-8 !w-8 !p-0 rounded-full justify-center" title="Stop">
              <StopIcon className="size-4 shrink-0" />
            </Button>
          ) : (
            <Button type="submit" variant="primary" disabled={!input.trim()} className="!min-h-0 !h-8 !w-8 !p-0 rounded-full justify-center" title="Send">
              <ArrowUpIcon className="size-4 shrink-0" />
            </Button>
          )}
        </div>
      </form>
      {!compact && <AiDisclaimer kind={disclaimerKind} size="inline" className="mx-auto max-w-3xl mt-2 text-center" />}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 bg-base-100" style={{ paddingBottom: bottomInset }}>
      {rail}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {compact && (
          <div ref={pickerRef} className="relative shrink-0 flex items-center gap-1 px-2 h-10 border-b border-base-300/70">
            <button onClick={() => setPickerOpen((o) => !o)} className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-base-200 text-sm text-base-content" title="Switch chat">
              <ChatBubbleLeftRightIcon className="size-4 text-base-content/50 shrink-0" />
              <span className="truncate">{activeThread?.title || "New chat"}</span>
              <ChevronDownIcon className={cn("size-3.5 text-base-content/50 shrink-0 transition-transform", pickerOpen && "rotate-180")} />
            </button>
            <button onClick={newThread} className="p-1.5 rounded-md hover:bg-base-200 text-base-content/60" title="New chat">
              <PlusIcon className="size-4" />
            </button>
            {onClose && (
              <button onClick={onClose} className="p-1.5 rounded-md hover:bg-base-200 text-base-content/60" title="Hide chat">
                <XMarkIcon className="size-4" />
              </button>
            )}
            {pickerOpen && (
              <div className="absolute left-2 right-2 top-full mt-1 z-20 rounded-xl border border-base-300 bg-base-100 shadow-lg overflow-hidden">
                <button
                  onClick={() => {
                    setPickerOpen(false);
                    newThread();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-base-content hover:bg-base-200 border-b border-base-300/70"
                >
                  <PlusIcon className="size-4" /> New chat
                </button>
                <ul className="max-h-72 overflow-auto py-1">
                  {threads.length === 0 && <li className="px-3 py-2 text-xs text-base-content/50">No chats yet</li>}
                  {threads.map((t) => (
                    <li key={t.id} className="group flex items-center">
                      <button
                        onClick={() => {
                          switchThread(t.id);
                          setPickerOpen(false);
                        }}
                        className={cn("flex-1 min-w-0 text-left px-3 py-2 hover:bg-base-200", t.id === threadId && "bg-base-200/60")}
                      >
                        <span className="flex items-center gap-2">
                          {t.id === threadId ? <CheckIcon className="size-3.5 text-blue-600 dark:text-blue-300 shrink-0" /> : <span className="size-3.5 shrink-0" />}
                          <span className="truncate text-sm text-base-content">{t.title || "Untitled"}</span>
                        </span>
                        <span className="block pl-[1.4rem] text-[11px] text-base-content/40">{relativeTime(t.last_message_at || t.created_at)}</span>
                      </button>
                      <button className="mr-2 p-1 rounded opacity-0 group-hover:opacity-100 text-base-content/40 hover:text-red-600 dark:hover:text-red-400" title="Archive" onClick={() => archive(t)}>
                        <TrashIcon className="size-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {!compact && (
          <div className="shrink-0 h-11 flex items-center gap-2 px-3 border-b border-base-300/70">
            <button className="p-1.5 rounded-md hover:bg-base-200 text-base-content/60" onClick={() => setRailOpen((o) => !o)} title="Chats">
              <Bars3BottomLeftIcon className="size-5" />
            </button>
            <span className="text-sm text-base-content/70 truncate">{activeThread?.title || "New chat"}</span>
            <span className="ml-auto text-[11px] text-base-content/40 hidden md:inline">{copy.tagline}</span>
          </div>
        )}

        <div ref={scrollRef} onScroll={onScroll} onWheel={onWheel} onTouchStart={onTouchStart} onTouchMove={onTouchMove} className="flex-1 overflow-y-auto">
          <div className={cn("relative mx-auto w-full", compact ? "px-3 py-3" : "max-w-3xl px-4 py-8")}>
            {empty ? (
              <div className={cn("flex flex-col items-center text-center", compact ? "pt-6" : "pt-[12vh]")}>
                <div className="size-12 rounded-2xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center mb-4">
                  <ChatBubbleLeftRightIcon className="size-6" />
                </div>
                <h2 className={cn("font-semibold text-base-content tracking-tight", compact ? "text-base" : "text-2xl")}>
                  {focus ? copy.focusEmptyTitle || copy.emptyTitle : compact ? copy.compactEmptyTitle : copy.emptyTitle}
                </h2>
                {focus && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 px-2.5 py-1 text-base-content/80 max-w-full">
                    {FocusIcon && <FocusIcon className="size-3 shrink-0 text-blue-500" />}
                    <span className="truncate">Focused on {focus.label}</span>
                    {copy.focusNote && <span className="text-base-content/50 shrink-0">· {copy.focusNote}</span>}
                  </p>
                )}
                <p className="mt-2 text-sm text-base-content/60 max-w-md">{copy.emptyBlurb}</p>
                <div className={cn("mt-8 grid gap-2 w-full", compact ? "grid-cols-1" : "sm:grid-cols-2")}>
                  {suggestions.slice(0, compact ? 3 : 6).map((s) => (
                    <button
                      key={s.text}
                      data-tour="chat-suggestion"
                      onClick={() => send(s.text)}
                      className="group flex items-start gap-3 text-left px-4 py-3 rounded-xl border border-base-300 bg-base-100 hover:bg-base-200 hover:border-base-content/20 transition-colors"
                    >
                      <s.icon className="size-4 mt-0.5 text-base-content/40 group-hover:text-blue-500 dark:group-hover:text-blue-300 shrink-0" />
                      <span className="text-sm text-base-content/80">{s.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {messages.map((m) =>
                  m.role === "user" ? (
                    <div key={m.id} className="flex justify-end">
                      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-base-200 px-4 py-2.5 text-[15px] leading-6 text-base-content whitespace-pre-wrap">{m.content}</div>
                    </div>
                  ) : (
                    <div key={m.id} className="flex gap-3">
                      <div className="size-7 shrink-0 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center mt-0.5">
                        <AvatarIcon className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        {m.streaming && !m.content && !(m.tools || []).some((t) => !t.done) && (
                          <div className="mb-2 text-[13px] font-medium">
                            <span className="shimmer-text">{m.tools && m.tools.length ? "Putting it together…" : "Thinking…"}</span>
                          </div>
                        )}
                        {m.tools && m.tools.length > 0 && (
                          <ol className="mb-2 space-y-0.5">
                            {m.tools.map((t, i) => {
                              const Icon = toolIcons[t.name] || MagnifyingGlassIcon;
                              return (
                                <li key={i} className="flex items-center gap-2 text-[12px] text-base-content/50">
                                  {t.done ? (
                                    <Icon className="size-3.5" />
                                  ) : (
                                    <span className="size-3.5 flex items-center justify-center">
                                      <span className="size-1.5 rounded-full bg-blue-500 animate-pulse" />
                                    </span>
                                  )}
                                  {t.done ? <span>{t.summary}</span> : <span className="shimmer-text text-[13px] font-medium">{t.summary}</span>}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                        <div data-tour="chat-answer" className="text-[15px] leading-7 text-base-content/90">
                          <StreamedMarkdown content={m.content} streaming={!!m.streaming} cite={cite} onGrow={followStream} />
                          {m.streaming && m.content && <span className="inline-block size-[9px] align-middle ml-1 -mt-0.5 bg-base-content/80 rounded-full" />}
                        </div>
                        {!m.streaming && m.citations && m.citations.length > 0 && (
                          <SourceChips citations={m.citations} content={m.content} onOpen={onOpenCitation} labelFor={labelFor} sourceIcon={sourceIcon} />
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
            <div ref={endRef} className="h-4" />
          </div>
        </div>
        <div className="relative h-0">
          <button
            type="button"
            onClick={jumpToLatest}
            tabIndex={streaming && !followingStream ? 0 : -1}
            aria-hidden={!(streaming && !followingStream)}
            className={cn(
              "absolute bottom-3 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium shadow bg-base-100 text-base-content/80 border border-base-300 hover:bg-base-200",
              "transition-[opacity,transform] duration-300 ease-out",
              streaming && !followingStream ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
            )}
          >
            <ArrowDownIcon className="size-3.5" /> Jump to latest
          </button>
        </div>

        {readOnlyNotice ? (
          <p className={cn("shrink-0 text-center text-xs text-base-content/50", compact ? "px-2 pb-3" : "px-4 pb-4")}>{readOnlyNotice}</p>
        ) : (
          composer
        )}
      </div>
      {hoverCite && <CitationPopover hover={hoverCite} onMouseEnter={cancelCiteClose} onMouseLeave={scheduleCiteClose} />}
    </div>
  );
};

// One chip per source rather than one per citation: a long answer can carry
// 40+ citations into the same six sources.
export function SourceChips({
  citations,
  content,
  onOpen,
  labelFor = defaultLabel,
  sourceIcon,
}: {
  citations: ChatCitation[];
  content: string;
  onOpen: (c: ChatCitation) => void;
  labelFor?: (c: ChatCitation) => string;
  sourceIcon?: (c: ChatCitation) => IconComponent;
}) {
  const used = citations.filter((c) => new RegExp(`\\[${c.n}\\]`).test(content));
  const groups = new Map<string, { first: ChatCitation; count: number }>();
  for (const c of used) {
    const key = c.callId || c.fileId || String(c.n);
    const g = groups.get(key);
    if (g) g.count++;
    else groups.set(key, { first: c, count: 1 });
  }
  if (!groups.size) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] text-base-content/40 mr-1">Sources</span>
      {[...groups.values()].map(({ first, count }) => {
        const Icon = sourceIcon ? sourceIcon(first) : DocumentTextIcon;
        return (
          <button
            key={first.n}
            onClick={() => onOpen(first)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-base-300 bg-base-100 hover:bg-base-200 text-[11px] text-base-content/70 transition-colors"
            title={first.label}
          >
            <Icon className="size-3 text-base-content/40" />
            <span>{labelFor(first)}</span>
            {count > 1 && <span className="tabular-nums text-base-content/40">×{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default ChatPanel;
